import { AuthConsumer, AuthProvider } from '@dashboard/context/AuthContext';
import { AIChatsProvider } from '@dashboard/context/AIContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom';

import ScrollToTop from '@/shared/components/ScrollToTop';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { PanelProvider } from '@dashboard/context/PanelContext';
import { TestProvider } from '@dashboard/context/TestContext';
import { DashboardToaster } from '@dashboard/context/ToastContext';
import { ThemeScope } from '@dashboard/components/ThemeScope';
import AuthLayout from '@dashboard/layouts/AuthLayout';
import MainLayout from '@dashboard/layouts/MainLayout';
import Account from '@dashboard/scenes/Account';

import Checkouts from '@dashboard/scenes/Checkouts';
import CustomerDetail from '@dashboard/scenes/CustomerDetail';
import Customers from '@dashboard/scenes/Customers';
import Dashboard from '@dashboard/scenes/Dashboard';
import SeatLocked from '@dashboard/scenes/SeatLocked/SeatLocked';
import DiscountsCoupons from '@dashboard/scenes/DiscountsCoupons';
import LinkInBio, { LinkInBioAnalytics, LinkInBioSettings } from '@dashboard/scenes/LinkInBio';
import DiscountsCouponsCreate from '@dashboard/scenes/DiscountsCouponsCreate';
import DiscountsCouponsDetailOverview from '@dashboard/scenes/DiscountsCouponsDetail/DiscountsCouponsDetailOverview';
import Events from '@dashboard/scenes/Events';
import Forms from '@dashboard/scenes/Forms';
import IntegrationsGoogleCallback from '@dashboard/scenes/Integrations/GoogleCallback';
import IntegrationsSlackCallback from '@dashboard/scenes/Integrations/SlackCallback';
import Logout from '@dashboard/scenes/Logout';
import PageCreate from '@dashboard/scenes/PageCreate';
import PageEdit from '@dashboard/scenes/PageEdit';
import { confirmNavigation } from '@dashboard/components/UnsavedChanges';
import PageNew from '@dashboard/scenes/PageNew';
import PageOverview from '@dashboard/scenes/PageOverview';

import PaymentDuePage from '@dashboard/scenes/PaymentDue';
import PaymentsCharges from '@dashboard/scenes/PaymentsCharges';
import PaymentsChargesDetailOverview from '@dashboard/scenes/PaymentsChargesDetail/PaymentsChargesDetailOverview';
import PaymentsSubscriptionsDetailOverview from '@dashboard/scenes/PaymentsSubscriptionsDetail/PaymentsSubscriptionsDetailOverview';
import PaymentsSubscriptions from '@dashboard/scenes/PaymentsSubscriptions';
import SellerConnectStripe from '@dashboard/scenes/SellerConnectStripe';
import SellerStart from '@dashboard/scenes/SellerStart';
import Settings from '@dashboard/scenes/Settings';
import Submissions from '@dashboard/scenes/Submissions';
import SubmissionsDetailOverview from '@dashboard/scenes/SubmissionsDetail/SubmissionsDetailOverview';
import Updates from '@dashboard/scenes/Updates';
import UpgradePage from '@dashboard/scenes/UpgradePage';
import Setup from '@dashboard/scenes/Setup/Setup';
import Admin from '@dashboard/admin/admin';
import NotFound from '@dashboard/scenes/NotFound';
import PublicLayout from '@dashboard/layouts/PublicLayout';
import { AdminGhostingProvider } from '@dashboard/context/AdminGhostingContext/AdminGhostingContext';
import PaymentsBookingsDetailOverview from '@/dashboard/scenes/PaymentsBookingsDetail';
import AttendeeDetail from '@/dashboard/scenes/AttendeeDetail/AttendeeDetail';
import PaymentsBookings from '@/dashboard/scenes/PaymentsBookings';
import AIGenerate from '@dashboard/scenes/AIGenerate';
import { CommandPalette, CommandPaletteProvider } from '@dashboard/components/CommandPalette';
import KeyboardShortcuts from '@dashboard/components/KeyboardShortcuts/KeyboardShortcuts';
import { AppErrorBoundary } from '@dashboard/components/AppErrorBoundary';
import { getStoreSetupPath, isStoreSetupExemptPath } from '@dashboard/utils/storeSetupRedirect';

import InviteStaffPage from '@dashboard/scenes/PageOverview/scenes/EventStaff/InviteStaffPage';
import AddBookingLayout from '@dashboard/scenes/PageOverview/scenes/AddBooking/AddBookingLayout';
import AddBookingSkeleton from '@dashboard/scenes/PageOverview/scenes/AddBooking/components/AddBookingSkeleton';
import { loginUrl } from '@dashboard/components/AuthForm/authUrl';

// Split out of the main bundle: its phone validation pulls in intl-tel-input's utils.
const LazyAddBookingPage = lazy(
  () => import('@dashboard/scenes/PageOverview/scenes/AddBooking/AddBookingPage'),
);
const AddBookingPage = props => (
  <Suspense
    fallback={
      <AddBookingLayout
        onBack={() =>
          props.history.push(
            props.location.state?.from ?? `/pages/${props.match.params.id}/overview`,
          )
        }
      >
        <AddBookingSkeleton />
      </AddBookingLayout>
    }
  >
    <LazyAddBookingPage {...props} />
  </Suspense>
);

// Create a client
const queryClient = new QueryClient();

const PrivateAppRoute = ({ component: Component, isFocused, isUpgradeRoute, ...rest }) => {
  const {
    isAuth,
    isAuthenticating,
    seller,
    hasActiveSubscription,
    isTrialing,
    isFree,
    isOwner,
    hasPastDueSubscription,
    isAdminLoggedInAsSeller,
  } = useAuth();
  const location = useLocation();

  if (!isAuth) {
    return <Redirect to="login" />;
  }

  // Redirect to account page if user has no store (only after auth is done loading)
  if (!isAuthenticating && !seller?._id && !location.pathname.startsWith('/account')) {
    return <Redirect to="/account/profile" />;
  }

  // Seat lockout (spec 2.5.6): a non-owner member of a Free workspace keeps their
  // membership but loses dashboard access until the owner upgrades. Renders inside
  // MainLayout — the sidebar stays (with its nav locked, like an expired sub), and
  // only the main content area shows the notice. /account (profile) and /updates
  // (What's new) stay reachable so they're never fully stranded.
  const isSeatLocked = seller?._id && isFree && !isOwner && !isAdminLoggedInAsSeller;
  const isSeatLockExempt =
    location.pathname.startsWith('/account') || location.pathname.startsWith('/updates');
  if (!isAuthenticating && isSeatLocked && !isSeatLockExempt) {
    return (
      <MainLayout {...rest} isFocused={isFocused}>
        <SeatLocked />
      </MainLayout>
    );
  }

  if (isUpgradeRoute && !isAdminLoggedInAsSeller) {
    if (isAuth && seller._id && !hasActiveSubscription && !isTrialing && !isFree) {
      return <Redirect to="/upgrade" />;
    }

    if (isAuth && seller._id && hasPastDueSubscription) {
      return <Redirect to="/payment-due" />;
    }
  }

  // Redirect to onboarding if needed
  const isStoreCreationIncomplete = !seller.industry || !seller.displayName || !seller.name;
  const onboardingRedirectCutoff = new Date('2025-12-30');
  const isNewSeller = seller.createdAt && new Date(seller.createdAt) > onboardingRedirectCutoff;

  // Redirect to onboarding, from any route, if seller created a new store but didn't set it up
  if (
    !isAdminLoggedInAsSeller &&
    seller._id &&
    isStoreCreationIncomplete &&
    isNewSeller &&
    !isStoreSetupExemptPath(location.pathname)
  ) {
    return <Redirect to={getStoreSetupPath(location)} />;
  }

  // Redirect to onboarding if seller hasn't completed it
  // Only redirect from main dashboard and primary list views, not other routes
  // Only applies to users who signed up after Dec 30, 2025
  const shouldRedirectToOnboarding =
    location.pathname === '/' ||
    location.pathname === '/checkouts' ||
    location.pathname === '/events' ||
    location.pathname === '/forms';
  if (
    !isAdminLoggedInAsSeller &&
    seller._id &&
    !seller.industry &&
    shouldRedirectToOnboarding &&
    isNewSeller
  ) {
    return <Redirect to="/seller/start" />;
  }

  return (
    <MainLayout {...rest} isFocused={isFocused}>
      {/* A crash clears on the next screen. The editor's tabs share one boundary, so moving
          between them keeps the preview mounted instead of rebuilding it each time. */}
      <AppErrorBoundary
        // One key per page editor and per detail opened inside a page, so moving between their
        // tabs keeps them mounted (the detail's back link remembers where it was opened from).
        key={location.pathname
          .replace(/^(\/pages\/[^/]+\/customize)\/.*$/, '$1')
          .replace(
            /^(\/pages\/[^/]+\/(?:payments|subscriptions|bookings|submissions|customers|leads|guests)\/[^/]+)\/.*$/,
            '$1',
          )}
      >
        <Route component={Component} isAuth={isAuth} {...rest} />
      </AppErrorBoundary>
    </MainLayout>
  );
};

const PublicRoute = ({ component: Component, ...rest }) => (
  <PublicLayout {...rest}>
    <Route {...rest} render={matchProps => <Component {...matchProps} />} />
  </PublicLayout>
);

const DashboardApp = ({ children }) => {
  return (
    <ThemeScope>
      <DashboardToaster />
      <QueryClientProvider client={queryClient}>
        <AuthProvider value={{ shouldAuthenticate: true }}>
          <CommandPaletteProvider>
            <TestProvider>
              <PanelProvider>
                <AdminGhostingProvider>
                  <AIChatsProvider>{children}</AIChatsProvider>
                </AdminGhostingProvider>
              </PanelProvider>
            </TestProvider>
          </CommandPaletteProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeScope>
  );
};

export default function dashboardRoutes() {
  return (
    <DashboardApp>
      <AuthConsumer>
        {({ isAuth, user }) => {
          if (!isAuth) {
            return <Redirect to={loginUrl(window.location.pathname, window.location.search)} />;
          }

          const basename = '/dashboard';

          return (
            <BrowserRouter basename={basename} getUserConfirmation={confirmNavigation}>
              <ScrollToTop />
              <CommandPalette />
              <KeyboardShortcuts />
              <Switch>
                <Route path="/ai-generate" component={AIGenerate} />

                <PrivateAppRoute path="/logout" component={Logout} isAuth={isAuth} user={user} />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/"
                  exact
                  component={Dashboard}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/new`}
                  component={PageNew}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/create`}
                  component={PageCreate}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <Redirect from="/pages/:id/results/:screen?" to="/pages/:id/:screen?" />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/:id/customize`}
                  component={PageEdit}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/:id/staff/invite`}
                  exact
                  component={InviteStaffPage}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/:id/add-booking`}
                  exact
                  component={AddBookingPage}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path={`/pages/:id`}
                  component={PageOverview}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />

                {/* Legacy /pages redirects to /checkouts (was unified list page) */}
                <Route exact path="/pages">
                  <Redirect to="/checkouts" />
                </Route>

                {/* Checkouts */}
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/checkouts/new"
                  component={PageNew}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/checkouts"
                  component={Checkouts}
                  isAuth={isAuth}
                  user={user}
                />

                {/* Events */}
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/events/new"
                  component={PageNew}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/events"
                  component={Events}
                  isAuth={isAuth}
                  user={user}
                />

                {/* Forms */}
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/forms/new"
                  component={PageNew}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/forms"
                  component={Forms}
                  isAuth={isAuth}
                  user={user}
                />

                {/* Payments, subscriptions, bookings moved out of /orders/* */}
                <PrivateAppRoute
                  isUpgradeRoute
                  path="/payments/:chargeId"
                  component={PaymentsChargesDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/payments"
                  component={PaymentsCharges}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/subscriptions/:subscriptionId"
                  component={PaymentsSubscriptionsDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/subscriptions"
                  component={PaymentsSubscriptions}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/bookings/:chargeId"
                  component={PaymentsBookingsDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path="/attendees/:ticketId"
                  component={AttendeeDetail}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/bookings"
                  component={PaymentsBookings}
                  isAuth={isAuth}
                  user={user}
                />

                {/* Redirects from old /orders/* paths */}
                <Route exact path="/orders/payments">
                  <Redirect to="/payments" />
                </Route>
                <Route
                  path="/orders/payments/:chargeId"
                  render={({ match }) => <Redirect to={`/payments/${match.params.chargeId}`} />}
                />
                <Route exact path="/orders/subscriptions">
                  <Redirect to="/subscriptions" />
                </Route>
                <Route
                  path="/orders/subscriptions/:subscriptionId"
                  render={({ match }) => (
                    <Redirect to={`/subscriptions/${match.params.subscriptionId}`} />
                  )}
                />
                <Route exact path="/orders/bookings">
                  <Redirect to="/bookings" />
                </Route>
                <Route
                  path="/orders/bookings/:chargeId"
                  render={({ match }) => <Redirect to={`/bookings/${match.params.chargeId}`} />}
                />
                <Route exact path="/orders">
                  <Redirect to="/payments" />
                </Route>

                <PrivateAppRoute
                  isUpgradeRoute
                  exact
                  path="/submissions"
                  component={Submissions}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/submissions/:submissionId"
                  component={SubmissionsDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/customers"
                  exact
                  component={Customers}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path="/customers/:id"
                  component={CustomerDetail}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/coupons"
                  exact
                  component={DiscountsCoupons}
                  isAuth={isAuth}
                  user={user}
                />
                <PrivateAppRoute
                  isUpgradeRoute
                  path="/coupons/new"
                  exact
                  component={DiscountsCouponsCreate}
                  isAuth={isAuth}
                  user={user}
                  isFocused
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/coupons/:id"
                  component={DiscountsCouponsDetailOverview}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/link-in-bio"
                  exact
                  component={LinkInBio}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/link-in-bio/analytics"
                  exact
                  component={LinkInBioAnalytics}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  isUpgradeRoute
                  path="/link-in-bio/settings"
                  exact
                  component={LinkInBioSettings}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  path="/settings"
                  component={Settings}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute path="/account" component={Account} isAuth={isAuth} user={user} />

                {/* Redirects for old billing routes */}
                <Route path="/billing/plan">
                  <Redirect to="/settings/plan" />
                </Route>
                <Route path="/billing/billing">
                  <Redirect to="/settings/billing" />
                </Route>
                <Route path="/billing">
                  <Redirect to="/settings/plan" />
                </Route>

                <PrivateAppRoute path="/setup" component={Setup} isAuth={isAuth} user={user} />

                <PrivateAppRoute path="/updates" component={Updates} isAuth={isAuth} user={user} />

                <PrivateAppRoute
                  path="/upgrade"
                  component={UpgradePage}
                  isAuth={isAuth}
                  user={user}
                />

                <PrivateAppRoute
                  path="/payment-due"
                  component={PaymentDuePage}
                  isAuth={isAuth}
                  user={user}
                />

                <Route path="/seller/start" component={SellerStart} />

                <Route path="/seller/connect-stripe">
                  <AuthLayout>
                    <SellerConnectStripe />
                  </AuthLayout>
                </Route>

                <Route path="/integrations/google/callback">
                  <AuthLayout>
                    <IntegrationsGoogleCallback />
                  </AuthLayout>
                </Route>

                <Route path="/integrations/slack/callback">
                  <AuthLayout>
                    <IntegrationsSlackCallback />
                  </AuthLayout>
                </Route>

                <Route path="/admin" component={Admin} />

                <PublicRoute component={NotFound} />
                <PublicRoute path="/404" component={NotFound} />
              </Switch>
            </BrowserRouter>
          );
        }}
      </AuthConsumer>
    </DashboardApp>
  );
}
